/**
 * Layer A — canonical creative identity + placements for persist and Meta materialization.
 * Single place for: normalize identity, read rules, resolve FB/IG positions (dual canonical,
 * defaults, legacy), strip orphan IG rules, build customization_rules from resolved arrays.
 */
import {
  AdSetStatus,
  PublisherPlatforms,
  type FacebookPositions,
  type InstagramPositions,
  type MarketingAd,
  type MarketingAdCreative,
  type PlatformCustomizationRule,
  type UpdateAdCreativeAd,
} from "../../interfaces/KYDMarketing";
import {
  DEFAULT_FACEBOOK_PLACEMENTS_FOR_META_CREATIVE,
  DEFAULT_INSTAGRAM_PLACEMENTS_FOR_META_CREATIVE,
  getCanonicalFacebookInstagramPlacementsForDualIdentity,
  normalizeInstagramPositionsForMetaApi,
} from "./campaigns";

const unique = <T>(items: T[]) => [...new Set(items)];

const PLACEMENT_CUSTOMIZATION_KEYS = [
  "facebook_positions",
  "instagram_positions",
  "messenger_positions",
] as const;

function customizationHasNonEmptyPlacements(c: Record<string, unknown>): boolean {
  for (const k of PLACEMENT_CUSTOMIZATION_KEYS) {
    const arr = c[k];
    if (Array.isArray(arr) && arr.length > 0) {
      return true;
    }
  }
  return false;
}

function customizationHasNonPlacementFields(c: Record<string, unknown>): boolean {
  const skip = new Set<string>(PLACEMENT_CUSTOMIZATION_KEYS);
  return Object.keys(c).some(
    (key) => !skip.has(key) && c[key] !== undefined
  );
}

/**
 * Normalize creative identity for API wire / UI: only non-empty Facebook page and Instagram actor
 * from the creative's `identity` are kept. Never merges from the ad set.
 */
export function normalizeCreativeIdentityForWire(
  creativeIdentity: MarketingAdCreative["identity"] | undefined
): MarketingAdCreative["identity"] | undefined {
  if (!creativeIdentity || typeof creativeIdentity !== "object") {
    return undefined;
  }
  const pageFromCreative =
    creativeIdentity[PublisherPlatforms.facebook]?.page_id?.trim() ?? "";
  const igFromCreative =
    creativeIdentity[
      PublisherPlatforms.instagram
    ]?.instagram_actor_id?.trim() ?? "";

  const out: NonNullable<MarketingAdCreative["identity"]> = {};
  if (pageFromCreative) {
    out[PublisherPlatforms.facebook] = { page_id: pageFromCreative };
  }
  if (igFromCreative) {
    out[PublisherPlatforms.instagram] = {
      instagram_actor_id: igFromCreative,
    };
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Removes `instagram_positions` from persisted rules when the creative has no Instagram actor.
 * Stale rules often appear after Instagram was cleared from `identity` without rewriting rules.
 */
export function stripInstagramPositionsFromCustomizationRules(
  rules: PlatformCustomizationRule[]
): PlatformCustomizationRule[] {
  return rules
    .map((rule) => {
      const c = rule.customization as Record<string, unknown>;
      if (!("instagram_positions" in c)) {
        return rule;
      }
      const { instagram_positions: _drop, ...rest } = c;
      return { ...rule, customization: rest } as PlatformCustomizationRule;
    })
    .filter((rule) => {
      const c = rule.customization as Record<string, unknown>;
      return (
        customizationHasNonEmptyPlacements(c) || customizationHasNonPlacementFields(c)
      );
    });
}

/**
 * Normalizes `identity` and drops Instagram placement rules when there is no Instagram actor,
 * so stored creatives cannot carry orphan `instagram_positions`.
 */
export function sanitizeMarketingAdCreativeForIdentityRuleParity(
  creative: MarketingAdCreative
): MarketingAdCreative {
  const normalizedIdentity = normalizeCreativeIdentityForWire(creative.identity);
  const hasIg = Boolean(
    normalizedIdentity?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim()
  );
  const base: MarketingAdCreative = {
    ...creative,
    identity: normalizedIdentity ?? creative.identity,
  };

  if (hasIg || !creative.customization_rules?.length) {
    return base;
  }

  const stripped = stripInstagramPositionsFromCustomizationRules(
    creative.customization_rules
  );
  return {
    ...base,
    customization_rules: stripped.length > 0 ? stripped : undefined,
  };
}

/** Applies {@link sanitizeMarketingAdCreativeForIdentityRuleParity} to the ad's creative. */
export function sanitizeMarketingAdForIdentityRuleParity(ad: MarketingAd): MarketingAd {
  if (!ad.creative) {
    return ad;
  }
  return {
    ...ad,
    creative: sanitizeMarketingAdCreativeForIdentityRuleParity(ad.creative),
  };
}

export type CreativePlacementSlice = Pick<
  MarketingAdCreative,
  "identity" | "customization_rules"
>;

/** True when the creative’s own `identity` includes a non-empty Instagram actor (no ad-set merge). */
export function creativeHasOwnInstagramActor(
  creative: CreativePlacementSlice | undefined
): boolean {
  const iden = normalizeCreativeIdentityForWire(creative?.identity);
  return Boolean(
    iden?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim()
  );
}

/**
 * `identity` + `customization_rules` sent to Meta for `asset_feed_spec` / placement resolution.
 *
 * **No borrowing actors between ads** — each stored creative keeps its own `identity`.
 *
 * When `includeInstagramIdentityOnWire` is false (this creative has no Instagram actor in
 * KYD), strips Instagram from the **Graph wire** for this creative only so Meta does not receive
 * `instagram_user_id` / IG placement rules without an actor. Stored KYD identity is unchanged
 * elsewhere; this is only the slice passed into Graph for this request.
 */
export function creativePlacementSliceForMetaAdSetCompatibility(
  creative: CreativePlacementSlice | undefined,
  includeInstagramIdentityOnWire: boolean
): CreativePlacementSlice {
  const c = creative ?? {};
  if (includeInstagramIdentityOnWire) {
    return {
      identity: c.identity,
      customization_rules: c.customization_rules,
    };
  }
  const normalized = normalizeCreativeIdentityForWire(c.identity);
  const pageId = normalized?.[PublisherPlatforms.facebook]?.page_id?.trim();
  const identityWire =
    pageId != null && pageId !== ""
      ? ({
          [PublisherPlatforms.facebook]: { page_id: pageId },
        } as MarketingAdCreative["identity"])
      : undefined;
  const stripped = stripInstagramPositionsFromCustomizationRules(
    c.customization_rules ?? []
  );
  return {
    identity: identityWire,
    customization_rules:
      stripped.length > 0 ? stripped : undefined,
  };
}

/** Raw FB/IG placement lists from `customization_rules` (Instagram only if identity has IG actor). */
export function extractRulesBasedPlacementsFromCreative(
  creative: CreativePlacementSlice | undefined
): {
  facebook: FacebookPositions[];
  instagram: InstagramPositions[];
} {
  const rules = creative?.customization_rules ?? [];
  const normalizedIdentity = normalizeCreativeIdentityForWire(creative?.identity);
  const hasIg = Boolean(
    normalizedIdentity?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim()
  );
  const facebook = rules
    .flatMap((rule) =>
      "facebook_positions" in rule.customization
        ? (rule.customization.facebook_positions ?? [])
        : []
    )
    .filter(Boolean) as FacebookPositions[];
  const instagram = hasIg
    ? (rules
        .flatMap((rule) =>
          "instagram_positions" in rule.customization
            ? (rule.customization.instagram_positions ?? [])
            : []
        )
        .filter(Boolean) as InstagramPositions[])
    : [];
  return { facebook, instagram };
}

export function extractPlacementsFromMarketingAd(ad: MarketingAd): {
  facebook: FacebookPositions[];
  instagram: InstagramPositions[];
} {
  return extractRulesBasedPlacementsFromCreative(ad.creative);
}

/**
 * Resolves wire/API placements + identity into canonical FB/IG position arrays.
 * Dual FB+IG identity → managed launch canonical lists; otherwise rules/defaults/legacy.
 */
export function resolvePlacementsForCreativeIdentity(params: {
  identity: MarketingAdCreative["identity"] | undefined;
  rulesBasedPlacements: {
    facebook: FacebookPositions[];
    instagram: InstagramPositions[];
  };
}): {
  normalizedIdentity: MarketingAdCreative["identity"] | undefined;
  facebook: FacebookPositions[];
  instagram: InstagramPositions[];
} {
  const normalizedIdentity = normalizeCreativeIdentityForWire(params.identity);
  const dual =
    getCanonicalFacebookInstagramPlacementsForDualIdentity(normalizedIdentity);
  const p = params.rulesBasedPlacements;

  if (dual) {
    return {
      normalizedIdentity,
      facebook: dual.facebook,
      instagram: dual.instagram,
    };
  }

  const hasPage = Boolean(
    normalizedIdentity?.[PublisherPlatforms.facebook]?.page_id?.trim()
  );
  const hasIg = Boolean(
    normalizedIdentity?.[
      PublisherPlatforms.instagram
    ]?.instagram_actor_id?.trim()
  );

  const facebook = (() => {
    if (hasPage) {
      return p.facebook.length > 0
        ? p.facebook
        : [...DEFAULT_FACEBOOK_PLACEMENTS_FOR_META_CREATIVE];
    }
    if (!normalizedIdentity && p.facebook.length > 0) {
      return p.facebook;
    }
    return [];
  })();

  const instagram = hasIg
    ? p.instagram.length > 0
      ? p.instagram
      : [...DEFAULT_INSTAGRAM_PLACEMENTS_FOR_META_CREATIVE]
    : [];

  return { normalizedIdentity, facebook, instagram };
}

/** Full resolution from stored creative `identity` + `customization_rules`. */
export function resolveCanonicalPlacementsFromMarketingAdCreative(
  creative: CreativePlacementSlice | undefined
): {
  normalizedIdentity: MarketingAdCreative["identity"] | undefined;
  facebook: FacebookPositions[];
  instagram: InstagramPositions[];
} {
  const c = creative ?? {};
  const rulesBased = extractRulesBasedPlacementsFromCreative(c);
  return resolvePlacementsForCreativeIdentity({
    identity: c.identity,
    rulesBasedPlacements: rulesBased,
  });
}

/** Persisted `customization_rules` from resolved placement arrays (Layer A encode). */
export function customizationRulesFromCanonicalPlacements(params: {
  facebook: FacebookPositions[];
  instagram: InstagramPositions[];
}): PlatformCustomizationRule[] {
  const { facebook, instagram } = params;
  const customization_rules: PlatformCustomizationRule[] = [];
  if (instagram.length > 0) {
    customization_rules.push({
      priority: 1,
      customization: { instagram_positions: instagram },
    });
  }
  if (facebook.length > 0) {
    customization_rules.push({
      priority: 1,
      customization: { facebook_positions: facebook },
    });
  }
  return customization_rules;
}

/**
 * Derives Meta **ad set** `targeting` placement fields (`publisher_platforms`, position lists).
 * Identity and `instagram_user_id` remain **per creative** at create time; this helper only unions
 * placements so the ad set can include Instagram whenever **any** live ad’s creative carries an
 * Instagram actor. Each ad without an IG actor contributes Facebook placements only.
 *
 * - Ads without a `creative` contribute nothing to the union.
 * - Ads with status {@link AdSetStatus.TO_DELETE} or {@link AdSetStatus.DELETED} are excluded.
 */
export function resolveAdsetPlacementTargetingFromAds(ads: Array<{
  creative?: CreativePlacementSlice;
  status?: AdSetStatus;
}>): {
  publisher_platforms: Array<"facebook" | "instagram">;
  facebook_positions: FacebookPositions[];
  instagram_positions: InstagramPositions[];
} {
  const list = (ads ?? []).filter(
    (a) =>
      a.status !== AdSetStatus.TO_DELETE && a.status !== AdSetStatus.DELETED
  );

  const facebookPositions: FacebookPositions[] = [];
  const instagramPositions: InstagramPositions[] = [];

  for (const ad of list) {
    const { facebook, instagram } =
      resolveCanonicalPlacementsFromMarketingAdCreative(ad.creative);
    facebookPositions.push(...facebook);
    if (creativeHasOwnInstagramActor(ad.creative)) {
      instagramPositions.push(...instagram);
    }
  }

  const uniqueFacebookPositions = unique(facebookPositions);
  const uniqueInstagramPositions = normalizeInstagramPositionsForMetaApi(
    instagramPositions
  );

  const publisher_platforms: PublisherPlatforms[] = [];
  if (uniqueFacebookPositions.length > 0) {
    publisher_platforms.push(PublisherPlatforms.facebook);
  }
  if (uniqueInstagramPositions.length > 0) {
    publisher_platforms.push(PublisherPlatforms.instagram);
  }

  return {
    publisher_platforms: publisher_platforms as Array<"facebook" | "instagram">,
    facebook_positions: uniqueFacebookPositions,
    instagram_positions: uniqueInstagramPositions,
  };
}

// ---------------------------------------------------------------------------
// Ad set–level Instagram parity (any ad with IG → all ads share one actor)
// ---------------------------------------------------------------------------

export type AdsetInstagramUnifyPlan =
  | { apply: false }
  | {
      apply: true;
      canonicalInstagramActorId: string;
      canonicalInstagramPlacements: InstagramPositions[];
    };

function normalizedInstagramPlacementsFromUpdateAd(
  ad: UpdateAdCreativeAd
): InstagramPositions[] {
  return normalizeInstagramPositionsForMetaApi(
    Array.from(new Set(ad.creative?.instagram_placements ?? [])).filter(
      Boolean
    ) as InstagramPositions[]
  );
}

function updateAdCreativeAdInstagramSelected(ad: UpdateAdCreativeAd): boolean {
  const iden = normalizeCreativeIdentityForWire(ad.identity);
  const actor =
    iden?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim() ?? "";
  return (
    Boolean(actor) || normalizedInstagramPlacementsFromUpdateAd(ad).length > 0
  );
}

function firstInstagramActorFromUpdateAdCreativeAd(
  ad: UpdateAdCreativeAd
): string | undefined {
  const iden = normalizeCreativeIdentityForWire(ad.identity);
  const v =
    iden?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim() ?? "";
  return v || undefined;
}

function marketingAdInstagramSelected(ad: MarketingAd): boolean {
  if (
    ad.status === AdSetStatus.TO_DELETE ||
    ad.status === AdSetStatus.DELETED
  ) {
    return false;
  }
  if (creativeHasOwnInstagramActor(ad.creative)) {
    return true;
  }
  const ig = extractRulesBasedPlacementsFromCreative(ad.creative).instagram;
  return normalizeInstagramPositionsForMetaApi(ig).length > 0;
}

function firstInstagramActorFromMarketingAd(ad: MarketingAd): string | undefined {
  const iden = normalizeCreativeIdentityForWire(ad.creative?.identity);
  const v =
    iden?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim() ?? "";
  return v || undefined;
}

/**
 * When any ad in the ad set uses Instagram (actor and/or placements), pick the first
 * non-empty Instagram actor in scan order (request body ads, then saved ads not in the body),
 * then align every ad in that merge to that actor and shared placement list.
 */
export function planAdsetInstagramUnification(params: {
  bodyAds: UpdateAdCreativeAd[];
  savedAdsNotInBody: MarketingAd[];
}): AdsetInstagramUnifyPlan {
  const saved = params.savedAdsNotInBody.filter(
    (a) =>
      a.status !== AdSetStatus.TO_DELETE && a.status !== AdSetStatus.DELETED
  );
  const body = params.bodyAds;

  const anySelected =
    body.some(updateAdCreativeAdInstagramSelected) ||
    saved.some(marketingAdInstagramSelected);

  if (!anySelected) {
    return { apply: false };
  }

  let canonicalActor: string | undefined;
  for (const ad of body) {
    const a = firstInstagramActorFromUpdateAdCreativeAd(ad);
    if (a) {
      canonicalActor = a;
      break;
    }
  }
  if (!canonicalActor) {
    for (const ad of saved) {
      const a = firstInstagramActorFromMarketingAd(ad);
      if (a) {
        canonicalActor = a;
        break;
      }
    }
  }

  if (!canonicalActor) {
    return { apply: false };
  }

  let canonicalPlacements: InstagramPositions[] | undefined;
  for (const ad of body) {
    if (!updateAdCreativeAdInstagramSelected(ad)) continue;
    const p = normalizedInstagramPlacementsFromUpdateAd(ad);
    if (p.length > 0) {
      canonicalPlacements = p;
      break;
    }
  }
  if (!canonicalPlacements) {
    for (const ad of saved) {
      if (!marketingAdInstagramSelected(ad)) continue;
      const p = normalizeInstagramPositionsForMetaApi(
        extractRulesBasedPlacementsFromCreative(ad.creative).instagram
      );
      if (p.length > 0) {
        canonicalPlacements = p;
        break;
      }
    }
  }

  const placements =
    canonicalPlacements && canonicalPlacements.length > 0
      ? canonicalPlacements
      : [...DEFAULT_INSTAGRAM_PLACEMENTS_FOR_META_CREATIVE];

  return {
    apply: true,
    canonicalInstagramActorId: canonicalActor,
    canonicalInstagramPlacements: placements,
  };
}

export function applyInstagramUnificationToUpdateAdCreativeAd(
  ad: UpdateAdCreativeAd,
  canonicalInstagramActorId: string,
  canonicalInstagramPlacements: InstagramPositions[]
): UpdateAdCreativeAd {
  const preNorm = normalizeCreativeIdentityForWire(ad.identity);
  const pageId = preNorm?.[PublisherPlatforms.facebook]?.page_id?.trim() ?? "";

  const mergedIdentity: NonNullable<UpdateAdCreativeAd["identity"]> = {
    [PublisherPlatforms.instagram]: {
      instagram_actor_id: canonicalInstagramActorId,
    },
  };
  if (pageId) {
    mergedIdentity[PublisherPlatforms.facebook] = { page_id: pageId };
  }

  return {
    ...ad,
    identity: mergedIdentity,
    creative: {
      ...ad.creative,
      instagram_placements: [...canonicalInstagramPlacements],
    },
  };
}

export function applyInstagramUnificationToMarketingAd(
  ad: MarketingAd,
  canonicalInstagramActorId: string,
  canonicalInstagramPlacements: InstagramPositions[]
): MarketingAd {
  if (!ad.creative) {
    return ad;
  }
  const creative = ad.creative;
  const preNorm = normalizeCreativeIdentityForWire(creative.identity);
  const pageId = preNorm?.[PublisherPlatforms.facebook]?.page_id?.trim() ?? "";

  const mergedIdentity: NonNullable<MarketingAdCreative["identity"]> = {
    [PublisherPlatforms.instagram]: {
      instagram_actor_id: canonicalInstagramActorId,
    },
  };
  if (pageId) {
    mergedIdentity[PublisherPlatforms.facebook] = { page_id: pageId };
  }

  const existingFb = extractRulesBasedPlacementsFromCreative(creative).facebook;

  const { normalizedIdentity, facebook, instagram } =
    resolvePlacementsForCreativeIdentity({
      identity: mergedIdentity,
      rulesBasedPlacements: {
        facebook: existingFb,
        instagram: canonicalInstagramPlacements,
      },
    });

  const customization_rules = customizationRulesFromCanonicalPlacements({
    facebook,
    instagram,
  });

  return {
    ...ad,
    creative: {
      ...creative,
      identity: normalizedIdentity,
      customization_rules,
    },
  };
}
